/**
 * Validation composer — collects field schemas and rules, composes a unified Zod schema.
 * Conditional rules are applied via superRefine at validation time.
 *
 * Three-Lane Architecture: Uses `evaluateFieldValidationState` from the shared
 * fieldStateEvaluator to determine visibility + required at validation time.
 */

import type React from 'react';
import { z } from 'zod';
import type { FormFieldMapping } from '../types/fieldMapping';
import type { FormRule } from '../types/block';
import type { FormLayoutNode, FormFieldsNode, FormEachNode, FormBatchTableNode } from '../types/layout';
import type { Condition } from '../types/conditions';
import { evaluateCondition, type ConditionContext } from '../evaluators/conditionEvaluator';
import { ResolverRegistry } from '../resolvers/resolverRegistry';
import { evaluateFieldValidationState } from './fieldStateEvaluator';
import { getPatternPreset } from '../resolvers/patternRegistry';
import { parseDateLocal } from '@packages/core';
import { isNonemptyHtml } from '@packages/shared/utils';

export interface ComposedValidation {
  schema: z.ZodTypeAny;
  fieldNames: string[];
  /** Soft-guard rules — evaluated separately; do not block submit. */
  warningRules: FormRule[];
}

export function composeValidation(
  layout: FormLayoutNode[],
  formRules: FormRule[],
  udfRequiredFieldsRef?: React.RefObject<Map<string, string>>,
  meta: Record<string, unknown> = {},
  /**
   * Pass any truthy string (e.g. `'items'`) when the composed schema is used
   * as a per-item schema inside a repeatable array wrapper
   * (`z.array(composed.schema)`). UDF ref paths in repeatable forms are stored
   * as form-absolute paths (`items.0.userDefinedFields.0.value`), but the
   * per-item superRefine receives item-level data. This flag causes
   * `runUdfValidation` to strip the leading `items.\d+.` segment from every
   * registered path before resolving the value and emitting the Zod issue.
   */
  udfItemPrefix?: string,
): ComposedValidation {
  const fieldMap = new Map<string, z.ZodTypeAny>();
  const fieldNames: string[] = [];
  const conditionalFields: { name: string; field: FormFieldMapping }[] = [];

  collectFieldsFromLayout(layout, fieldMap, fieldNames, conditionalFields);

  const unconditionalRules = formRules.filter((r) => !r.condition && !r.warning && !r.enabled);
  const conditionalRules = formRules.filter((r) => (r.condition || r.enabled) && !r.warning);
  const warningRules = formRules.filter((r) => r.warning);

  applyRulesToSchema(fieldMap, unconditionalRules);

  const shape = buildNestedShape(fieldMap);

  const baseSchema = z.object(shape).passthrough();

  const needsRuleValidation = conditionalRules.length > 0 || conditionalFields.length > 0;

  // Run conditional rules on RAW input, independent of base schema parse outcome.
  //
  // Why not baseSchema.superRefine()?
  // Zod skips the parent superRefine when a nested field fails type checks
  // (e.g. RhfInputNumber stores number 1 but def schema expects z.string()).
  // That produces a two-pass UX: field-level errors first, rule errors only
  // after those clear.
  //
  // Why not z.intersection(baseSchema, z.any().superRefine(...))?
  // When the base branch succeeds but rules fail, Zod adds a spurious
  // "Intersection results could not be merged" issue at the root path.
  //
  // Fix: outer z.any().superRefine() manually safeParse's the base schema,
  // forwards every base issue via ctx.addIssue, then always evaluates rules
  // against the raw input — single pass, all errors collected together.
  const hasUdf = udfRequiredFieldsRef != null;

  // When UDF or conditional rules exist, wrap the base schema with
  // attachIndependentRuleValidation so that ALL validation (base fields,
  // conditional rules, AND UDF required checks) runs in a single pass.
  // Without this, z.object(shape).superRefine(udf) skips the UDF refinement
  // when any base field fails — producing a two-pass UX where UDF errors
  // only appear after all base errors are fixed.
  const needsIndependentWrapper = needsRuleValidation || hasUdf;

  const composedSchema = needsIndependentWrapper
    ? attachIndependentRuleValidation(baseSchema, (data, ctx) => {
        if (needsRuleValidation) {
          runConditionalValidation(data, conditionalFields, conditionalRules, ctx, meta);
        }
        if (hasUdf) {
          runUdfValidation(data as Record<string, unknown>, udfRequiredFieldsRef!, ctx, udfItemPrefix);
        }
      })
    : baseSchema;

  return {
    schema: composedSchema,
    fieldNames,
    warningRules,
  };
}

function runUdfValidation(
  data: Record<string, unknown>,
  udfRequiredFieldsRef: React.RefObject<Map<string, string>>,
  ctx: z.RefinementCtx,
  itemPrefix?: string,
): void {
  if (!udfRequiredFieldsRef.current?.size) return;
  // When running inside a per-item superRefine (repeatable forms), the ref
  // stores full form-absolute paths like `items.0.userDefinedFields.0.value`.
  // Strip the `items.N.` prefix (any index) so paths resolve against per-item data.
  const repeatableItemPrefixRe = itemPrefix != null ? /^items\.\d+\./ : null;
  for (const [fieldPath, errorMsg] of udfRequiredFieldsRef.current) {
    const relativePath = repeatableItemPrefixRe
      ? fieldPath.replace(repeatableItemPrefixRe, '')
      : fieldPath;
    const fieldValue = getNestedValue(data, relativePath);
    if (isEmpty(fieldValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: relativePath.split('.'),
        message: errorMsg,
      });
    }
  }
}

function applyRequiredConstraint(
  existing: z.ZodTypeAny,
  message: string,
): z.ZodTypeAny {
  const requiredMessage = message || 'Required field';
  const coerceToString = (val: unknown) => {
    if (val == null) return '';
    if (typeof val === 'number' && !Number.isNaN(val)) return String(val);
    return val;
  };

  if (existing instanceof z.ZodString) {
    // For required strings, always prepend a min(1) check with "Required field" message.
    // The .pipe() ensures the existing schema's validations (maxLength, pattern, etc.)
    // still run after the required check passes.
    return z.preprocess(
      coerceToString,
      z.string().min(1, requiredMessage).pipe(existing),
    );
  }

  if (isNumberLikeSchema(existing)) {
    return z.preprocess(normalizeOptionalNumberValue, existing);
  }

  if (existing instanceof z.ZodArray) {
    return z.preprocess(
      (val) => (val == null ? [] : val),
      existing.min(1, requiredMessage),
    );
  }

  return existing;
}

function attachIndependentRuleValidation(
  baseSchema: z.ZodTypeAny,
  validateRules: (data: Record<string, unknown>, ctx: z.RefinementCtx) => void,
): z.ZodTypeAny {
  return z.any().superRefine((input, ctx) => {
    const data = (input ?? {}) as Record<string, unknown>;

    const baseResult = baseSchema.safeParse(input);
    if (!baseResult.success) {
      for (const issue of baseResult.error.issues) {
        ctx.addIssue(issue);
      }
    }

    validateRules(data, ctx);
  });
}

function runConditionalValidation(
  data: Record<string, unknown>,
  conditionalFields: { name: string; field: FormFieldMapping }[],
  conditionalRules: FormRule[],
  ctx: z.RefinementCtx,
  meta: Record<string, unknown> = {},
): void {
  for (const { name, field } of conditionalFields) {
    const { visible, required: shouldCheckRequired } = evaluateFieldValidationState(
      field,
      data,
      meta,
      () => undefined,
    );

    if (!visible) continue;

    if (shouldCheckRequired) {
      const fieldValue = getNestedValue(data, name);
      if (isFieldEmpty(field, fieldValue, data)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: name.split('.'),
          message: 'Required field',
        });
      }
    }
  }

  for (const rule of conditionalRules) {
    if (!isRuleEnabled(rule, data)) continue;
    if (rule.condition) {
      const conditionCtx = buildConditionContextFromData(data, rule, meta);
      if (!evaluateCondition(rule.condition, conditionCtx)) continue;
    }

    const ruleTarget = rule.target;
    const targetField = conditionalFields.find((cf) => cf.name === ruleTarget);
    if (targetField) {
      const visCondition = targetField.field.when?.visible ?? targetField.field.conditionalVisible;
      if (visCondition) {
        const visCtx = buildConditionContextFromData(data, rule, meta);
        if (!evaluateCondition(visCondition, visCtx)) continue;
      }
    }

    const fieldValue = getNestedValue(data, rule.target);
    const error = validateRule(rule, fieldValue, data);
    if (error) {
      const message = rule.messageField
        ? (String(getNestedValue(data, rule.messageField) ?? '') || error)
        : error;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: rule.target.split('.'),
        message,
      });
    }
  }
}

function isRuleEnabled(rule: FormRule, data: Record<string, unknown>): boolean {
  if (!rule.enabled) return true;
  const flag = getNestedValue(data, rule.enabled);
  return flag === true || flag === 'true' || (typeof flag === 'string' && flag.trim() !== '' && flag !== 'false');
}

function buildConditionContextFromData(
  data: Record<string, unknown>,
  rule: FormRule,
  meta: Record<string, unknown> = {},
): ConditionContext {
  const targetValue = getNestedValue(data, rule.target);
  return {
    fieldValue: targetValue,
    values: data,
    meta,
    getOptionProp: () => undefined,
  };
}

function validateRule(
  rule: FormRule,
  fieldValue: unknown,
  allValues: Record<string, unknown>,
): string | null {
  const ruleType = typeof rule.rule === 'object' ? rule.rule.rule : rule.rule;

  if (ruleType === 'remote') {
    // Remote validation: async server-side check via ActionRegistry.executeData().
    // Triggered asynchronously in superRefine when the target field changes.
    // The rule.rule object carries: { rule: 'remote', actionId: string, paramField?: string }.
    // Remote rules are collected by the conditional validation pass and executed
    // via Promise.all in the async superRefine path. When the server returns an
    // error, ctx.addIssue() is called with the rule.message.
    return null;
  }

  switch (ruleType) {
    // ── Presence ──────────────────────────────────────────────
    case 'required':
      return isEmpty(fieldValue) ? rule.message : null;

    case 'htmlNotEmpty':
      return isNonemptyHtml(fieldValue) ? null : rule.message;

    // ── String rules — skip when value is empty ──────────────
    case 'pattern': {
      if (isEmptyString(fieldValue)) return null;
      // Resolve pattern: patternPreset takes precedence over raw patternValue string
      let resolvedPattern: RegExp | null = null;
      if (rule.patternPreset) {
        const preset = getPatternPreset(rule.patternPreset);
        if (preset) resolvedPattern = preset.pattern;
      } else if (rule.patternValue) {
        try {
          resolvedPattern = new RegExp(rule.patternValue);
        } catch {
          resolvedPattern = null;
        }
      }
      if (resolvedPattern && typeof fieldValue === 'string') {
        if (!resolvedPattern.test(fieldValue)) return rule.message;
      }
      return null;
    }

    case 'minLength':
      if (isEmptyString(fieldValue)) return null;
      if (rule.minValue !== undefined && typeof fieldValue === 'string') {
        return fieldValue.length < rule.minValue ? rule.message : null;
      }
      return null;

    case 'maxLength':
      if (isEmptyString(fieldValue)) return null;
      if (rule.maxValue !== undefined && typeof fieldValue === 'string') {
        return fieldValue.length > rule.maxValue ? rule.message : null;
      }
      return null;

    // ── Date rules — skip when either side is empty ──────────
    case 'dateAfter': {
      if (!fieldValue || !rule.comparedTo) return null;
      const target = parseDateLocal(String(fieldValue)).getTime();
      const compared = getComparedDate(rule.comparedTo, allValues);
      if (isNaN(target) || isNaN(compared)) return null;
      return target <= compared ? rule.message : null;
    }

    case 'dateBefore': {
      if (!fieldValue || !rule.comparedTo) return null;
      const target = parseDateLocal(String(fieldValue)).getTime();
      const compared = getComparedDate(rule.comparedTo, allValues);
      if (isNaN(target) || isNaN(compared)) return null;
      return target >= compared ? rule.message : null;
    }

    // ── Numeric rules — skip when value is empty/non-numeric ─
    case 'greaterThan': {
      if (isEmpty(fieldValue) || rule.comparedTo === undefined) return null;
      const numVal = toSafeNumber(fieldValue);
      const comparedVal = toSafeNumber(resolveFieldOrLiteral(rule.comparedTo, allValues));
      if (numVal === null || comparedVal === null) return null;
      return numVal <= comparedVal ? rule.message : null;
    }

    case 'greaterThanOrEqual': {
      if (isEmpty(fieldValue) || rule.comparedTo === undefined) return null;
      const numVal = toSafeNumber(fieldValue);
      const comparedVal = toSafeNumber(resolveFieldOrLiteral(rule.comparedTo, allValues));
      if (numVal === null || comparedVal === null) return null;
      return numVal < comparedVal ? rule.message : null;
    }

    case 'lessThan': {
      if (isEmpty(fieldValue) || rule.comparedTo === undefined) return null;
      const numVal = toSafeNumber(fieldValue);
      const comparedVal = toSafeNumber(resolveFieldOrLiteral(rule.comparedTo, allValues));
      if (numVal === null || comparedVal === null) return null;
      return numVal >= comparedVal ? rule.message : null;
    }

    case 'lessThanOrEqual': {
      if (isEmpty(fieldValue) || rule.comparedTo === undefined) return null;
      const numVal = toSafeNumber(fieldValue);
      const comparedVal = toSafeNumber(resolveFieldOrLiteral(rule.comparedTo, allValues));
      if (numVal === null || comparedVal === null) return null;
      return numVal > comparedVal ? rule.message : null;
    }

    // ── Array rules — treat null/undefined as empty array ────
    case 'arrayUnique': {
      if (!Array.isArray(fieldValue) || !rule.uniqueKey) return null;
      const seen = new Set<unknown>();
      for (const item of fieldValue) {
        const val = (item as Record<string, unknown>)?.[rule.uniqueKey];
        if (val != null && seen.has(val)) return rule.message;
        if (val != null) seen.add(val);
      }
      return null;
    }

    case 'arraySum': {
      if (!Array.isArray(fieldValue) || !rule.sumField || rule.equals === undefined) return null;
      const sum = fieldValue.reduce((acc, item) => {
        return acc + (Number((item as Record<string, unknown>)?.[rule.sumField!]) || 0);
      }, 0);
      return sum !== rule.equals ? rule.message : null;
    }

    case 'arrayMinItems': {
      const arr = Array.isArray(fieldValue) ? fieldValue : [];
      return (rule.minValue !== undefined && arr.length < rule.minValue) ? rule.message : null;
    }

    case 'arrayMaxItems': {
      const arr = Array.isArray(fieldValue) ? fieldValue : [];
      return (rule.maxValue !== undefined && arr.length > rule.maxValue) ? rule.message : null;
    }

    case 'arrayContains': {
      const arr = Array.isArray(fieldValue) ? fieldValue : [];
      if (!rule.uniqueKey) return null;
      const hasItem = arr.some(
        (item) => (item as Record<string, unknown>)?.[rule.uniqueKey!] != null,
      );
      return hasItem ? null : rule.message;
    }

    case 'custom': {
      if (!rule.customId) return null;
      const validator = ResolverRegistry.getValidation(rule.customId);
      if (!validator) return null;
      const ctx: ConditionContext = {
        fieldValue,
        values: allValues,
        meta: {},
        getOptionProp: () => undefined,
      };
      return validator(fieldValue, allValues, ctx);
    }

    default:
      return null;
  }
}

function getComparedDate(comparedTo: string, allValues: Record<string, unknown>): number {
  const fieldValue = getNestedValue(allValues, comparedTo);
  if (fieldValue != null && String(fieldValue).trim() !== '') {
    return parseDateLocal(String(fieldValue)).getTime();
  }
  const literal = parseDateLocal(comparedTo).getTime();
  return isNaN(literal) ? NaN : literal;
}

/**
 * Resolve a rule operand that can be either a field path or a numeric literal.
 * Returns the resolved raw value.
 */
function resolveFieldOrLiteral(operand: string | number, allValues: Record<string, unknown>): unknown {
  if (typeof operand === 'number') return operand;
  const fieldValue = getNestedValue(allValues, operand);
  if (fieldValue !== undefined) return fieldValue;
  return operand;
}

/**
 * Convert a value to a number safely — returns null for empty/non-numeric values.
 * Unlike `Number()`, this rejects empty strings and whitespace-only strings
 * instead of silently converting them to 0.
 */
function toSafeNumber(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return isNaN(value) ? null : value;
  if (typeof value === 'string') {
    const trimmed = value.trim()?.replaceAll(',', '');
    if (trimmed === '') return null;
    const num = Number(trimmed);
    return isNaN(num) ? null : num;
  }
  return null;
}

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Empty check that accounts for file-upload existing files.
 * For file-upload fields with existingIdField, checks staged + existing - removed.
 * For all other fields, delegates to plain isEmpty.
 */
function isFieldEmpty(
  field: FormFieldMapping,
  fieldValue: unknown,
  formData: Record<string, unknown>,
): boolean {
  const cfg = field.fileUpload;
  if (!cfg?.existingIdField) return isEmpty(fieldValue);

  const stagedCount = Array.isArray(fieldValue) ? fieldValue.length : 0;
  if (stagedCount > 0) return false;

  const existingIds = getNestedValue(formData, cfg.existingIdField);
  const existingCount = Array.isArray(existingIds) ? existingIds.length
    : (typeof existingIds === 'string' && existingIds ? 1 : 0);
  if (existingCount === 0) return true;

  const removedIds = cfg.removedIdsField
    ? getNestedValue(formData, cfg.removedIdsField)
    : undefined;
  const removedCount = Array.isArray(removedIds) ? removedIds.length : 0;

  return (existingCount - removedCount) <= 0;
}

function isEmptyString(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

function collectFieldsFromLayout(
  nodes: FormLayoutNode[],
  fieldMap: Map<string, z.ZodTypeAny>,
  fieldNames: string[],
  conditionalFields?: { name: string; field: FormFieldMapping }[],
): void {
  for (const node of nodes) {
    switch (node.type) {
      case 'fields':
        collectFieldMappings(
          node.fields.filter((f): f is FormFieldMapping => !('type' in f)),
          fieldMap, fieldNames, conditionalFields,
        );
        break;
      case 'each':
        collectEachFields(node, fieldMap, fieldNames);
        break;
      case 'batch-table':
        collectBatchTableFields(node, fieldMap, fieldNames);
        break;
      case 'section':
      case 'grid':
      case 'stack':
      case 'surface':
        collectFieldsFromLayout(node.children, fieldMap, fieldNames, conditionalFields);
        break;
      case 'tabs':
        for (const tab of node.tabs) {
          collectFieldsFromLayout(tab.children, fieldMap, fieldNames, conditionalFields);
        }
        break;
      case 'conditional':
        collectFieldsFromLayout(node.children, fieldMap, fieldNames, conditionalFields);
        if (node.else) {
          collectFieldsFromLayout(node.else, fieldMap, fieldNames, conditionalFields);
        }
        break;
      case 'column-group':
        for (const group of node.groups) {
          collectFieldMappings(
            group.fields.filter((f): f is FormFieldMapping => !('type' in f)),
            fieldMap, fieldNames, conditionalFields,
          );
        }
        break;
      case 'divider':
      case 'custom':
      case 'action-button':
      case 'udf':
      case 'scroll-agreement':
      case 'html-template':
      case 'prose':
        break;
    }
  }
}

function collectFieldMappings(
  fields: FormFieldMapping[],
  fieldMap: Map<string, z.ZodTypeAny>,
  fieldNames: string[],
  conditionalFields?: { name: string; field: FormFieldMapping }[],
): void {
  for (const field of fields) {
    if (field.hidden) continue;
    const fieldSchema = buildFieldSchema(field);
    fieldMap.set(field.name, fieldSchema);
    fieldNames.push(field.name);

    if (isConditionalField(field)) {
      conditionalFields?.push({ name: field.name, field });
    }
  }
}

function collectBatchTableFields(
  node: FormBatchTableNode,
  fieldMap: Map<string, z.ZodTypeAny>,
  fieldNames: string[],
): void {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const column of node.columns) {
    shape[column.key] = z.unknown().optional();
  }
  const itemSchema = z.object(shape).passthrough();
  fieldMap.set(node.name, z.array(itemSchema));
  fieldNames.push(node.name);
}

function collectEachFields(
  node: FormEachNode,
  fieldMap: Map<string, z.ZodTypeAny>,
  fieldNames: string[],
): void {
  if (node.primitiveItem) {
    const itemField = node.itemFields.find((f) => !f.hidden) ?? node.itemFields[0];
    let itemSchema: z.ZodTypeAny = itemField ? buildFieldSchema(itemField) : z.string();

    let arraySchema: z.ZodTypeAny = z.array(itemSchema);
    if (node.minItems !== undefined) {
      arraySchema = (arraySchema as z.ZodArray<typeof itemSchema>).min(node.minItems);
    }
    if (node.maxItems !== undefined) {
      arraySchema = (arraySchema as z.ZodArray<typeof itemSchema>).max(node.maxItems);
    }

    fieldMap.set(node.name, arraySchema);
    fieldNames.push(node.name);
    return;
  }

  const itemFieldMap = new Map<string, z.ZodTypeAny>();
  for (const field of node.itemFields) {
    if (field.hidden) continue;
    itemFieldMap.set(field.name, buildFieldSchema(field));
  }

  if (node.itemLayout?.length) {
    collectNestedEachInItemLayout(node.itemLayout, itemFieldMap);
  }

  const itemRules = node.itemRules ?? [];
  const unconditionalItemRules = itemRules.filter((r) => !r.condition && !r.warning);
  const conditionalItemRules = itemRules.filter((r) => r.condition && !r.warning);
  applyRulesToSchema(itemFieldMap, unconditionalItemRules);

  const itemShape: Record<string, z.ZodTypeAny> = Object.fromEntries(itemFieldMap);
  let itemSchema: z.ZodTypeAny = z.object(itemShape).passthrough();

  if (conditionalItemRules.length > 0) {
    itemSchema = attachIndependentRuleValidation(itemSchema, (data, ctx) => {
      runItemRuleValidation(data, conditionalItemRules, ctx);
    });
  }

  let arraySchema: z.ZodTypeAny = z.array(itemSchema);

  if (node.minItems !== undefined) {
    arraySchema = (arraySchema as z.ZodArray<typeof itemSchema>).min(node.minItems);
  }
  if (node.maxItems !== undefined) {
    arraySchema = (arraySchema as z.ZodArray<typeof itemSchema>).max(node.maxItems);
  }

  fieldMap.set(node.name, arraySchema);
  fieldNames.push(node.name);
}

/** Collect nested `each` nodes from itemLayout into the parent item shape. */
function collectNestedEachInItemLayout(
  nodes: FormLayoutNode[],
  itemFieldMap: Map<string, z.ZodTypeAny>,
): void {
  for (const node of nodes) {
    switch (node.type) {
      case 'each':
        collectEachFieldsIntoMap(node, itemFieldMap, node.name);
        break;
      case 'fields':
        collectFieldMappingsIntoMap(
          node.fields.filter((f): f is FormFieldMapping => !('type' in f)),
          itemFieldMap,
        );
        break;
      case 'section':
      case 'grid':
      case 'stack':
      case 'surface':
        collectNestedEachInItemLayout(node.children, itemFieldMap);
        break;
      case 'tabs':
        for (const tab of node.tabs) {
          collectNestedEachInItemLayout(tab.children, itemFieldMap);
        }
        break;
      case 'conditional':
        collectNestedEachInItemLayout(node.children, itemFieldMap);
        if (node.else) collectNestedEachInItemLayout(node.else, itemFieldMap);
        break;
      case 'column-group':
        for (const group of node.groups) {
          collectFieldMappingsIntoMap(
            group.fields.filter((f): f is FormFieldMapping => !('type' in f)),
            itemFieldMap,
          );
        }
        break;
      default:
        break;
    }
  }
}

function collectEachFieldsIntoMap(
  node: FormEachNode,
  targetMap: Map<string, z.ZodTypeAny>,
  pathPrefix: string,
): void {
  const nestedItemMap = new Map<string, z.ZodTypeAny>();
  for (const field of node.itemFields) {
    if (field.hidden) continue;
    nestedItemMap.set(field.name, buildFieldSchema(field));
  }
  if (node.itemLayout?.length) {
    collectNestedEachInItemLayout(node.itemLayout, nestedItemMap);
  }

  const itemRules = node.itemRules ?? [];
  applyRulesToSchema(nestedItemMap, itemRules.filter((r) => !r.condition && !r.warning));

  const itemShape = Object.fromEntries(nestedItemMap);
  let itemSchema: z.ZodTypeAny = z.object(itemShape).passthrough();

  const conditionalItemRules = itemRules.filter((r) => r.condition && !r.warning);
  if (conditionalItemRules.length > 0) {
    itemSchema = attachIndependentRuleValidation(itemSchema, (data, ctx) => {
      runItemRuleValidation(data, conditionalItemRules, ctx);
    });
  }

  let arraySchema: z.ZodTypeAny = z.array(itemSchema);
  if (node.minItems !== undefined) {
    arraySchema = (arraySchema as z.ZodArray<typeof itemSchema>).min(node.minItems);
  }
  if (node.maxItems !== undefined) {
    arraySchema = (arraySchema as z.ZodArray<typeof itemSchema>).max(node.maxItems);
  }

  targetMap.set(pathPrefix, arraySchema);
}

function collectFieldMappingsIntoMap(
  fields: FormFieldMapping[],
  targetMap: Map<string, z.ZodTypeAny>,
): void {
  for (const field of fields) {
    if (field.hidden) continue;
    targetMap.set(field.name, buildFieldSchema(field));
  }
}

function runItemRuleValidation(
  data: Record<string, unknown>,
  rules: FormRule[],
  ctx: z.RefinementCtx,
): void {
  for (const rule of rules) {
    if (!rule.condition || !evaluateCondition(rule.condition, buildConditionContextFromData(data, rule))) {
      continue;
    }
    const fieldValue = getNestedValue(data, rule.target);
    const error = validateRule(rule, fieldValue, data);
    if (error) {
      const message = rule.messageField
        ? (String(getNestedValue(data, rule.messageField) ?? '') || error)
        : error;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: rule.target.split('.'),
        message,
      });
    }
  }
}

/**
 * Unwrap ZodDefault / ZodOptional wrappers to get the inner type.
 * FormLoader wraps array fields as z.array(z.string()).optional().default([]).
 * We need the inner ZodArray to check instanceof and apply .min(1) for required.
 */
function unwrapZodSchema(schema: z.ZodTypeAny): z.ZodTypeAny {
  if (schema instanceof z.ZodDefault) return unwrapZodSchema(schema._def.innerType);
  if (schema instanceof z.ZodOptional) return unwrapZodSchema(schema._def.innerType);
  if (schema instanceof z.ZodNullable) return unwrapZodSchema(schema._def.innerType);
  return schema;
}

/** Unwrap optional/nullable/default/effects layers to the core Zod type. */
function unwrapToCoreSchema(schema: z.ZodTypeAny): z.ZodTypeAny {
  let current = schema;
  for (;;) {
    if (current instanceof z.ZodDefault) {
      current = current._def.innerType;
      continue;
    }
    if (current instanceof z.ZodOptional) {
      current = current._def.innerType;
      continue;
    }
    if (current instanceof z.ZodNullable) {
      current = current._def.innerType;
      continue;
    }
    if (current instanceof z.ZodEffects) {
      current = current._def.schema;
      continue;
    }
    break;
  }
  return current;
}

function isNumberLikeSchema(schema: z.ZodTypeAny): boolean {
  return unwrapToCoreSchema(schema) instanceof z.ZodNumber;
}

/**
 * Single source of truth: does this field have any conditional state that
 * requires runtime evaluation (visibility, required-when, permissions)?
 *
 * Used by BOTH `buildFieldSchema` (to wrap as optional) and
 * `collectFieldMappings` (to push into conditionalFields for runtime checks).
 * Keeping this in ONE function prevents the two call-sites from drifting apart.
 */
function isConditionalField(field: FormFieldMapping): boolean {
  return !!(
    field.when?.visible || field.when?.required ||
    field.conditionalVisible || field.conditionalRequired || field.permissions ||
    isFileUploadWithExisting(field)
  );
}

/** File-upload fields with existing-file tracking need form-level validation.
 *  Zod only sees the staged-files array; the adapter's Controller.rules.validate
 *  is ignored when a Zod resolver is active. This routes the required check
 *  through runConditionalValidation which has access to full form data. */
function isFileUploadWithExisting(field: FormFieldMapping): boolean {
  return !!(field.required && field.fileUpload?.existingIdField);
}

function buildFieldSchema(field: FormFieldMapping): z.ZodTypeAny {
  const zodSchema = extractZodFromFieldDef(field.def);

  if (isConditionalField(field)) {
    return wrapOptionalField(zodSchema);
  }

  if (field.required) {
    // Unwrap ZodDefault/ZodOptional/ZodNullable wrappers (e.g. array fields from FormLoader)
    // before checking instanceof to properly apply required constraints.
    const innerSchema = unwrapZodSchema(zodSchema);

    if (innerSchema instanceof z.ZodString || zodSchema instanceof z.ZodString) {
      const strSchema = zodSchema instanceof z.ZodString ? zodSchema : innerSchema as z.ZodString;
      const coerceToString = (val: unknown) => {
        if (val == null) return '';
        if (typeof val === 'number' && !Number.isNaN(val)) return String(val);
        return val;
      };
      // Always prepend a min(1) "Required field" check. The .pipe() ensures
      // the existing schema's validations (minLength, maxLength, pattern, etc.)
      // still run after the required check passes.
      return z.preprocess(
        coerceToString,
        z.string().min(1, 'Required field').pipe(zodSchema instanceof z.ZodString ? zodSchema : strSchema),
      );
    }
    if (isNumberLikeSchema(zodSchema)) {
      return z.preprocess(normalizeOptionalNumberValue, zodSchema);
    }
    // Array field: apply min(1) for required validation.
    // Item values may be string | number | boolean (the backend contract owns the type).
    // z.array(z.any()) from FormLoader still unwraps to ZodArray here.
    if (innerSchema instanceof z.ZodArray) {
      // File-upload fields with existing-file tracking: skip Zod min(1).
      // The RhfFileUpload Controller.rules.validate checks both staged AND
      // existing files — Zod only sees staged files and would false-positive.
      if (field.fileUpload?.existingIdField) {
        return wrapOptionalField(zodSchema);
      }
      return z.preprocess(
        (val) => (val == null ? [] : val),
        (innerSchema as z.ZodArray<z.ZodTypeAny>).min(1, 'Required field'),
      );
    }
    // All other required types (boolean, enum, etc.): preprocess null values
    // to produce a user-friendly "Required field" message instead of Zod's
    // cryptic "Expected boolean, received null". The innerSchema (unwrapped)
    // validates the actual value once non-null.
    return z.preprocess(
      (val) => val,
      z.any().refine((val) => val != null, { message: 'Required field' }).pipe(innerSchema),
    );
  }

  return wrapOptionalField(zodSchema);
}

/**
 * Make optional fields treat cleared inputs as empty.
 * String schemas: blank → undefined (avoids regex failures on "").
 * Number schemas: blank/NaN → undefined (avoids type errors on "").
 */
function normalizeFieldInputValue(val: unknown): unknown {
  if (val == null) return undefined;
  if (typeof val === 'string' && val.trim() === '') return undefined;
  if (typeof val === 'number' && !Number.isNaN(val)) return String(val);
  return val;
}

function normalizeOptionalNumberValue(val: unknown): unknown {
  if (val == null || val === '') return undefined;
  if (typeof val === 'number') return Number.isNaN(val) ? undefined : val;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed === '') return undefined;
    // Strip thousands-separator commas that editPrecision display formatting adds.
    // e.g. "44,477" → 44477. Without stripping, Number("44,477") → NaN and Zod
    // rejects with "Expected number, received string".
    const stripped = trimmed.replace(/,/g, '');
    const num = Number(stripped);
    return Number.isNaN(num) ? val : num;
  }
  return val;
}

function wrapOptionalField(schema: z.ZodTypeAny): z.ZodTypeAny {
  // Unwrap ZodNullable to inspect the inner type — FormLoader always wraps string
  // schemas in .nullable() so `schema instanceof z.ZodString` would miss them.
  const coreSchema = schema instanceof z.ZodNullable
    ? (schema as z.ZodNullable<z.ZodTypeAny>)._def.innerType
    : schema;

  if (coreSchema instanceof z.ZodString) {
    const hasChecks = (coreSchema as z.ZodString)._def.checks?.length > 0;
    if (hasChecks) {
      // Wrap the full original schema (preserving the nullable wrapper from FormLoader)
      // inside a preprocess so empty/whitespace inputs are converted to undefined and
      // skip the regex checks entirely. Without this, optional fields with noBlank/
      // noDoubleSpace/charset declared in domain.json would fire on any empty input.
      return z.preprocess(
        normalizeFieldInputValue,
        schema.optional().nullable(),
      );
    }
  }
  if (isNumberLikeSchema(schema)) {
    return z.preprocess(normalizeOptionalNumberValue, schema.optional().nullable());
  }
  if (schema instanceof z.ZodArray) {
    return z.preprocess(
      (val) => (val == null || (typeof val === 'string' && val.trim() === '') ? [] : val),
      schema.optional().nullable(),
    );
  }
  return schema.optional().nullable();
}

function extractZodFromFieldDef(def: { schema: unknown }): z.ZodTypeAny {
  if (def.schema && typeof def.schema === 'object' && '_def' in (def.schema as object)) {
    return def.schema as z.ZodTypeAny;
  }
  return z.any();
}

/**
 * Apply unconditional rules to the base Zod schema.
 * Rules that add string constraints (pattern, minLength, maxLength) use
 * `wrapOptionalField` so cleared/empty fields don't fail validation
 * when the field is not required.
 */
function applyRulesToSchema(
  fieldMap: Map<string, z.ZodTypeAny>,
  rules: FormRule[],
): void {
  for (const rule of rules) {
    const existing = fieldMap.get(rule.target);
    if (!existing) continue;
    if (typeof rule.rule === 'object') continue;

    switch (rule.rule) {
      case 'required':
        fieldMap.set(rule.target, applyRequiredConstraint(existing, rule.message));
        break;
      case 'htmlNotEmpty':
        fieldMap.set(
          rule.target,
          existing.refine((val) => isNonemptyHtml(val), { message: rule.message }),
        );
        break;
      case 'minLength':
        if (rule.minValue !== undefined) {
          fieldMap.set(rule.target, wrapOptionalField(
            z.string().min(rule.minValue, rule.message),
          ));
        }
        break;
      case 'maxLength':
        if (rule.maxValue !== undefined) {
          fieldMap.set(rule.target, wrapOptionalField(
            z.string().max(rule.maxValue, rule.message),
          ));
        }
        break;
      case 'pattern': {
        // patternPreset takes precedence over raw patternValue string
        let resolvedRegex: RegExp | null = null;
        if (rule.patternPreset) {
          const presetDef = getPatternPreset(rule.patternPreset);
          if (presetDef) resolvedRegex = presetDef.pattern;
        } else if (rule.patternValue) {
          try {
            resolvedRegex = new RegExp(rule.patternValue);
          } catch {
            resolvedRegex = null;
          }
        }
        if (resolvedRegex) {
          fieldMap.set(rule.target, wrapOptionalField(
            z.string().regex(resolvedRegex, rule.message),
          ));
        }
        break;
      }
    }
  }
}

/**
 * Builds a nested shape object suitable for z.object() from dot-separated field paths.
 * Example: 'participant.firstName' → { participant: { firstName: z.string() } }
 */
function buildNestedShape(fieldMap: Map<string, z.ZodTypeAny>): Record<string, z.ZodTypeAny> {
  const tree: Record<string, unknown> = {};

  for (const [path, schema] of fieldMap) {
    const parts = path.split('.');
    let current = tree;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      if (!current[key] || typeof current[key] !== 'object' || current[key] instanceof z.ZodType) {
        current[key] = {};
      }
      current = current[key] as Record<string, unknown>;
    }
    current[parts[parts.length - 1]] = schema;
  }

  return treeToZodShape(tree);
}

function treeToZodShape(tree: Record<string, unknown>): Record<string, z.ZodTypeAny> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const [key, value] of Object.entries(tree)) {
    if (value instanceof z.ZodType) {
      shape[key] = value;
    } else if (typeof value === 'object' && value !== null) {
      shape[key] = z.object(treeToZodShape(value as Record<string, unknown>)).passthrough().optional().nullable();
    }
  }
  return shape;
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}
